import type { GameCondition, GameRuleAction, GameStateFieldSchema } from "../game/game-definition.js";
import {
  ROQEET_GAME_DEFAULT_ACTIONS,
  ROQEET_GAME_DOCUMENT_KIND,
  ROQEET_GAME_FORMAT_VERSION,
  ROQEET_GAME_RQT_VERSION,
  validateRoqeetGameDocument,
  type RoqeetGameChoice,
  type RoqeetGameDocument,
  type RoqeetGameDocumentIssue,
  type RoqeetGameNode,
  type RoqeetGameSceneNode,
} from "../game/roqeet-game-document.js";
import {
  parseRoqeet,
  evaluateRoqeetExpression,
  type RoqeetChoicesNode,
  type RoqeetDocument,
  type RoqeetGameMetaNode,
  type RoqeetNode,
  type RoqeetParseError,
  type RoqeetPromptNode,
  type RoqeetSceneNode,
  type RoqeetStateDeclaration,
  type RoqeetStateSchemaNode,
  type RoqeetValue,
} from "./index.js";

export interface RqtToRoqeetGameDocumentOptions {
  fileId?: string;
}

export interface RqtToRoqeetGameDocumentResult {
  ast: RoqeetDocument;
  document: RoqeetGameDocument;
  parseErrors: RoqeetParseError[];
  validationIssues: RoqeetGameDocumentIssue[];
}

export function rqtToRoqeetGameDocument(
  source: string,
  options: RqtToRoqeetGameDocumentOptions = {},
): RqtToRoqeetGameDocumentResult {
  const ast = parseRoqeet(source, { fileId: options.fileId });
  const document = roqeetDocumentToRoqeetGameDocument(ast, options);
  return {
    ast,
    document,
    parseErrors: ast.errors,
    validationIssues: validateRoqeetGameDocument(document),
  };
}

export function roqeetDocumentToRoqeetGameDocument(
  ast: RoqeetDocument,
  options: RqtToRoqeetGameDocumentOptions = {},
): RoqeetGameDocument {
  const meta = findFirstNode(ast.nodes, "GameMeta");
  const prompt = findNarrationPrompt(ast.nodes);
  const scenes = ast.nodes.filter((node): node is RoqeetSceneNode => node.type === "Scene").map(lowerSceneNode);
  const stateNodes = ast.nodes.filter((node): node is RoqeetStateSchemaNode => node.type === "StateSchema");
  const stateSchema = stateNodes.map((node) => lowerStateField(node.field));
  const initialState = stateNodes.reduce<Record<string, unknown>>((values, node) => {
    values[node.field.key] = cloneRoqeetValue(node.field.initial);
    return values;
  }, {});

  const nodes: RoqeetGameNode[] = [
    lowerMetaNode(meta, options),
    { type: "game.initialState", values: initialState },
    { type: "game.narrationGuide", text: prompt ? renderText(prompt.body) : "" },
    {
      type: "game.aiContract",
      aiOutput: "narration-and-proposed-actions",
      engineRole: "validate-and-apply",
      allowedActions: [...ROQEET_GAME_DEFAULT_ACTIONS],
      invalidAction: "ignore-and-report",
    },
  ];

  if (stateSchema.length) {
    nodes.splice(2, 0, { type: "game.stateSchema", fields: stateSchema });
  }
  nodes.push(...scenes);

  return {
    kind: ROQEET_GAME_DOCUMENT_KIND,
    rqtVersion: ROQEET_GAME_RQT_VERSION,
    formatVersion: ROQEET_GAME_FORMAT_VERSION,
    ...(scenes[0]?.id ? { startSceneId: scenes[0].id } : {}),
    nodes,
  };
}

function lowerMetaNode(
  node: RoqeetGameMetaNode | undefined,
  options: RqtToRoqeetGameDocumentOptions,
): Extract<RoqeetGameNode, { type: "game.meta" }> {
  const values = node?.values ?? {};
  const title = stringValue(values["title"]) ?? titleFromFileId(options.fileId) ?? "Untitled Game";
  const meta: Extract<RoqeetGameNode, { type: "game.meta" }> = { type: "game.meta", title };
  const description = stringValue(values["description"]);
  const icon = stringValue(values["icon"]);
  if (description !== undefined) meta.description = description;
  if (icon !== undefined) meta.icon = icon;
  return meta;
}

function lowerStateField(field: RoqeetStateDeclaration): GameStateFieldSchema {
  return {
    key: field.key,
    valueType: field.valueType,
    default: cloneRoqeetValue(field.initial) as GameStateFieldSchema["default"],
    ...(field.min !== undefined ? { min: field.min } : {}),
    ...(field.max !== undefined ? { max: field.max } : {}),
    ...(field.values !== undefined ? { values: [...field.values] } : {}),
    ...(field.mutableBy !== undefined ? { mutableBy: field.mutableBy } : {}),
    ...(field.mutable !== undefined ? { mutable: field.mutable } : {}),
    ...(field.derived !== undefined ? { derived: field.derived } : {}),
    ...(field.transient !== undefined ? { transient: field.transient } : {}),
    ...(field.label !== undefined ? { label: field.label } : {}),
  };
}

function lowerSceneNode(node: RoqeetSceneNode): RoqeetGameSceneNode {
  const title = stringValue(node.props["title"]);
  const choices = collectChoices(node.body);
  return {
    type: "game.scene",
    id: node.id,
    ...(title !== undefined ? { title } : {}),
    content: renderText(node.body),
    ...(choices.length ? { choices } : {}),
  };
}

interface ChoiceLoweringContext {
  when: GameCondition[];
  effects: GameRuleAction[];
}

const EMPTY_CHOICE_CONTEXT: ChoiceLoweringContext = {
  when: [],
  effects: [],
};

function collectChoices(nodes: RoqeetNode[], context: ChoiceLoweringContext = EMPTY_CHOICE_CONTEXT): RoqeetGameChoice[] {
  const choices: RoqeetGameChoice[] = [];
  let pendingEffects = [...context.effects];
  for (const node of nodes) {
    switch (node.type) {
      case "Set":
      case "Change": {
        const effect = lowerChoiceEffect(node);
        if (effect) pendingEffects.push(effect);
        break;
      }
      case "Choices":
        choices.push(...lowerChoicesNode(node, { when: context.when, effects: pendingEffects }));
        pendingEffects = [];
        break;
      case "If": {
        const when = lowerChoiceConditions(node.condition);
        const thenContext = when.length
          ? { when: [...context.when, ...when], effects: pendingEffects }
          : { when: context.when, effects: pendingEffects };
        choices.push(...collectChoices(node.then, thenContext));
        for (const clause of node.elseIf ?? []) {
          const clauseWhen = lowerChoiceConditions(clause.condition);
          const clauseContext = clauseWhen.length
            ? { when: [...context.when, ...clauseWhen], effects: pendingEffects }
            : { when: context.when, effects: pendingEffects };
          choices.push(...collectChoices(clause.then, clauseContext));
        }
        if (node.else) {
          const elseWhen = invertChoiceConditions(when);
          const elseContext = elseWhen.length
            ? { when: [...context.when, ...elseWhen], effects: pendingEffects }
            : { when: context.when, effects: pendingEffects };
          choices.push(...collectChoices(node.else, elseContext));
        }
        break;
      }
      case "Random":
        choices.push(...collectChoices(node.items, { when: context.when, effects: pendingEffects }));
        break;
      case "Dialogue":
      case "Prompt":
        choices.push(...collectChoices(node.body, { when: context.when, effects: pendingEffects }));
        break;
    }
  }
  return choices;
}

function lowerChoicesNode(node: RoqeetChoicesNode, context: ChoiceLoweringContext): RoqeetGameChoice[] {
  return node.items.map((item, index) => {
    const to = item.action?.trim();
    return {
      id: choiceId(item.label, index),
      label: item.label,
      ...(to ? { to } : {}),
      ...(context.when.length ? { when: cloneConditions(context.when) } : {}),
      ...(context.effects.length ? { effects: cloneActions(context.effects) } : {}),
    };
  });
}

function lowerChoiceEffect(node: Extract<RoqeetNode, { type: "Set" | "Change" }>): GameRuleAction | undefined {
  if (node.type === "Set") {
    const value = staticRuleValue(node.expression);
    return value === undefined ? undefined : { op: "set", key: node.target, value };
  }

  const value = staticRuleValue(node.amount);
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return {
    op: node.op === "increase" ? "inc" : "dec",
    key: node.target,
    value,
  };
}

function lowerChoiceConditions(source: string): GameCondition[] {
  const parts = splitConditionParts(source);
  const conditions: GameCondition[] = [];
  for (const part of parts) {
    const condition = lowerChoiceCondition(part);
    if (!condition) return [];
    conditions.push(condition);
  }
  return conditions;
}

function splitConditionParts(source: string): string[] {
  return source
    .split(/\s*(?:&&|\band\b)\s*/u)
    .map((part) => stripOuterParens(part.trim()))
    .filter(Boolean);
}

function lowerChoiceCondition(source: string): GameCondition | undefined {
  const trimmed = stripOuterParens(source.trim());
  const falsy = /^!\s*([A-Za-z_][A-Za-z0-9_.-]*)$/u.exec(trimmed)
    ?? /^not\s+([A-Za-z_][A-Za-z0-9_.-]*)$/u.exec(trimmed);
  if (falsy?.[1]) return { key: falsy[1], op: "falsy" };

  if (/^[A-Za-z_][A-Za-z0-9_.-]*$/u.test(trimmed)) return { key: trimmed, op: "truthy" };

  const match = /^([A-Za-z_][A-Za-z0-9_.-]*)\s*(>=|<=|==|!=|>|<)\s*(.+)$/u.exec(trimmed);
  if (!match) return undefined;
  const value = staticRuleValue(match[3]!.trim());
  if (value === undefined) return undefined;
  return {
    key: match[1]!,
    op: conditionOp(match[2]!),
    value,
  };
}

function conditionOp(operator: string): GameCondition["op"] {
  switch (operator) {
    case ">=":
      return "gte";
    case "<=":
      return "lte";
    case ">":
      return "gt";
    case "<":
      return "lt";
    case "!=":
      return "neq";
    case "==":
    default:
      return "eq";
  }
}

function invertChoiceConditions(conditions: GameCondition[]): GameCondition[] {
  if (conditions.length !== 1) return [];
  const condition = conditions[0]!;
  switch (condition.op) {
    case "truthy":
      return [{ key: condition.key, op: "falsy" }];
    case "falsy":
      return [{ key: condition.key, op: "truthy" }];
    case "eq":
      return [{ key: condition.key, op: "neq", value: condition.value }];
    case "neq":
      return [{ key: condition.key, op: "eq", value: condition.value }];
    case "gte":
      return [{ key: condition.key, op: "lt", value: condition.value }];
    case "lte":
      return [{ key: condition.key, op: "gt", value: condition.value }];
    case "gt":
      return [{ key: condition.key, op: "lte", value: condition.value }];
    case "lt":
      return [{ key: condition.key, op: "gte", value: condition.value }];
  }
}

function staticRuleValue(source: string): GameRuleAction["value"] | undefined {
  try {
    const value = evaluateRoqeetExpression(source);
    if (typeof value === "string" || typeof value === "boolean") return value;
    if (typeof value === "number" && Number.isFinite(value)) return value;
  } catch {
    return undefined;
  }
  return undefined;
}

function stripOuterParens(source: string): string {
  if (!source.startsWith("(") || !source.endsWith(")")) return source;
  return source.slice(1, -1).trim();
}

function cloneConditions(conditions: GameCondition[]): GameCondition[] {
  return conditions.map((condition) => ({ ...condition }));
}

function cloneActions(actions: GameRuleAction[]): GameRuleAction[] {
  return actions.map((action) => ({ ...action }));
}

function renderText(nodes: RoqeetNode[]): string {
  const lines: string[] = [];
  for (const node of nodes) {
    const text = renderNode(node);
    if (text.trim()) lines.push(text.trim());
  }
  return lines.join("\n");
}

function renderNode(node: RoqeetNode): string {
  switch (node.type) {
    case "Text":
      return node.text;
    case "Dialogue":
      return `${node.speaker}: ${renderText(node.body)}`;
    case "Prompt":
      return renderText(node.body);
    case "If": {
      const parts = [renderText(node.then)];
      for (const clause of node.elseIf ?? []) parts.push(renderText(clause.then));
      if (node.else) parts.push(renderText(node.else));
      return parts.filter(Boolean).join("\n");
    }
    case "Random":
      return renderText(node.items);
    case "Image":
      return `[image: ${node.asset}]`;
    case "Roll":
      return `[roll: ${node.notation}]`;
    case "Set":
    case "Change":
    case "Push":
    case "Unset":
    case "Choices":
    case "Button":
    case "Scene":
    case "Comment":
    case "Env":
    case "GameMeta":
    case "StateSchema":
      return "";
  }
}

function findNarrationPrompt(nodes: RoqeetNode[]): RoqeetPromptNode | undefined {
  const prompts = nodes.filter((node): node is RoqeetPromptNode => node.type === "Prompt");
  return prompts.find((node) => node.name === "guide" || node.name === "narrationGuide") ?? prompts[0];
}

function findFirstNode<T extends RoqeetNode["type"]>(
  nodes: RoqeetNode[],
  type: T,
): Extract<RoqeetNode, { type: T }> | undefined {
  return nodes.find((node): node is Extract<RoqeetNode, { type: T }> => node.type === type);
}

function stringValue(value: RoqeetValue | undefined): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function choiceId(label: string, index: number): string {
  const slug = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return slug || `choice-${index + 1}`;
}

function titleFromFileId(fileId: string | undefined): string | undefined {
  if (!fileId) return undefined;
  const base = fileId.split(/[\\/]/).pop()?.replace(/\.[^.]+$/, "").trim();
  return base || undefined;
}

function cloneRoqeetValue(value: RoqeetValue): unknown {
  if (Array.isArray(value)) return value.map(cloneRoqeetValue);
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, cloneRoqeetValue(item)]));
  }
  return value;
}
